import React, { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  formatDate,
  formatDateTime,
  formatDuration,
  formatCurrency,
  formatTime,
} from '../utils/formatters';

const ACTION_STYLES = {
  create: { dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-800 border border-blue-200' },
  update: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800 border border-amber-200' },
  approve: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-800 border border-green-200' },
  reject: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-800 border border-red-200' },
  manager_note: { dot: 'bg-purple-500', badge: 'bg-purple-100 text-purple-800 border border-purple-200' },
};

const DEFAULT_STYLE = {
  dot: 'bg-gray-400',
  badge: 'bg-gray-100 text-gray-800 border border-gray-200',
};

const FIELD_LABELS = {
  title: 'Título',
  description: 'Descrição',
  entry_date: 'Data',
  entry_type: 'Tipo',
  attendance_type: 'Atendimento',
  service_status: 'Status do serviço',
  approval_status: 'Aprovação',
  total_hours: 'Horas',
  hourly_rate: 'Valor/hora',
  total_amount: 'Valor total',
  consultant: 'Consultor',
  client: 'Cliente',
  project: 'Projeto',
  periods: 'Períodos',
};

const formatPeriods = (periods) => {
  if (!Array.isArray(periods) || periods.length === 0) return '—';
  return periods
    .map((p) => `${formatTime(p.start || p.start_time)} - ${formatTime(p.end || p.end_time)}`)
    .join(', ');
};

const formatValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'periods') return formatPeriods(value);
  if (field === 'entry_date') return formatDate(value);
  if (field === 'total_hours') return formatDuration(value);
  if (field === 'hourly_rate' || field === 'total_amount') return formatCurrency(value);
  if (typeof value === 'object') return value.name || JSON.stringify(value);
  return String(value);
};

const Timeline = ({ entryId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/time-entries/${entryId}/history/`);
        const data = response.data.results || response.data;
        setHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error('Erro ao carregar histórico.');
      } finally {
        setLoading(false);
      }
    };
    if (entryId) fetchHistory();
  }, [entryId]);

  const renderChanges = (item) => {
    if (item.action === 'create') {
      return <p className="text-sm text-gray-600 mt-2">Lançamento criado.</p>;
    }

    const changes = item.changes || {};
    const fields = Object.keys(changes);
    if (fields.length === 0) return null;

    return (
      <ul className="mt-2 space-y-1">
        {fields.map((field) => {
          const diff = changes[field] || {};
          return (
            <li key={field} className="text-sm text-gray-600">
              <span className="font-medium text-gray-700">{FIELD_LABELS[field] || field}:</span>{' '}
              <span className="text-gray-500 line-through">{formatValue(field, diff.old)}</span>
              {' → '}
              <span className="text-gray-800">{formatValue(field, diff.new)}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-6 inline-flex items-center gap-2">
        <History size={18} className="text-gray-500" />
        Histórico de alterações
      </h3>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : history.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">Nenhum registro de histórico.</p>
      ) : (
        <ol className="relative border-l-2 border-gray-200 ml-2 space-y-6">
          {history.map((item) => {
            const style = ACTION_STYLES[item.action] || DEFAULT_STYLE;
            return (
              <li key={item.id} className="ml-6 relative">
                <span
                  className={`absolute -left-[31px] top-4 w-3.5 h-3.5 rounded-full border-2 border-white ${style.dot}`}
                />
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.badge}`}
                    >
                      {item.action_display || item.action}
                    </span>
                    <span className="text-sm text-gray-700 font-medium">
                      {item.changed_by?.name || 'Sistema'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatDateTime(item.changed_at)}
                    </span>
                  </div>
                  {item.notes && (
                    <p className="text-sm text-gray-600 mt-2 italic">"{item.notes}"</p>
                  )}
                  {renderChanges(item)}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default Timeline;
